export type Role = "root" | "staff" | "account-owner" | "vision-reviewer";

/** The shared-catalog curation capability: root, plus the internal staff role. */
export const canCurateCatalog = (role: string | undefined): boolean =>
	role === "root" || role === "staff";

/** Vision training-set curation: root, staff, and the vision-reviewer role. */
export const canCurateVision = (role: string | undefined): boolean =>
	role === "root" || role === "staff" || role === "vision-reviewer";

/** Full root capability. `platform.User.HasRoot()` on the server is this test. */
export const isRoot = (role: string | undefined): boolean => role === "root";

/**
 * The Sudo area: all of it for root, the catalog-curation and vision-curation
 * surfaces for staff, the vision-curation surface for a vision-reviewer.
 */
export const canSeeSudo = (role: string | undefined): boolean =>
	canCurateCatalog(role) || canCurateVision(role);

/**
 * The /_/sudo/* routes the catalog-curation capability opens. These are the
 * shared-catalog tools; every other Sudo tool is root-only, and the server
 * refuses a caller without the capability on those routes whatever the client
 * shows.
 */
export const staffSudoPathPrefixes: string[] = [
	"/_/sudo/catalog",
	"/_/sudo/distributors",
	"/_/sudo/supply-chain",
];

/** The /_/sudo/* routes the vision training-set curation capability opens. */
export const visionSudoPathPrefixes: string[] = ["/_/sudo/vision-tasks"];

/** The Sudo landing page (the tool list itself) every sudo-capable role may open. */
const sudoLandingPath = "/_/sudo";

/** matchesPrefix reports whether `path` is one of `prefixes` or sits beneath one. */
const matchesPrefix = (prefixes: string[], path: string): boolean =>
	prefixes.some((prefix) => path === prefix || path.startsWith(prefix + "/"));

/**
 * canOpenSudoPath reports whether `role` may open the sudo route `path`.
 * Root passes everywhere; every sudo-capable role passes on the landing page;
 * otherwise a role passes only on the prefixes its capabilities grant — the
 * catalog prefixes for a catalog curator, the vision prefix for a vision
 * curator — and everyone else passes nowhere.
 */
export const canOpenSudoPath = (
	role: string | undefined,
	path: string,
): boolean => {
	if (isRoot(role)) return true;
	if (!canSeeSudo(role)) return false;
	if (path === sudoLandingPath) return true;
	return (
		(canCurateCatalog(role) && matchesPrefix(staffSudoPathPrefixes, path)) ||
		(canCurateVision(role) && matchesPrefix(visionSudoPathPrefixes, path))
	);
};
